/** biome-ignore-all lint/suspicious/noConsole: Required for logging functionality */
import { SpanKind, SpanStatusCode, trace } from "@opentelemetry/api";

/**
 * Log levels for structured logging
 */
// biome-ignore lint/style/noEnum: Required for log level constants
export enum LogLevel {
  DEBUG = "debug",
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

/**
 * Base log entry structure for Vercel Runtime Logs compatibility
 */
type BaseLogEntry = {
  timestamp: string;
  level: LogLevel;
  message: string;
  service: string;
  environment: string;
  traceId?: string;
  spanId?: string;
  [key: string]: unknown;
};

/**
 * Client-side API call log entry
 */
interface APILogEntry extends BaseLogEntry {
  api: {
    endpoint: string;
    method: string;
    duration: number;
    status: number;
    statusText?: string;
    errorCode?: string;
    userId?: string;
    requestId?: string;
  };
}

/**
 * User interaction log entry
 */
interface UserInteractionLogEntry extends BaseLogEntry {
  interaction: {
    type:
      | "click"
      | "navigation"
      | "form_submit"
      | "search"
      | "filter"
      | "cart_action";
    element?: string;
    page: string;
    userId?: string;
    metadata?: Record<string, unknown>;
  };
}

/**
 * Client-side structured logger for WillPower Store
 */
class ClientLogger {
  private readonly serviceName: string;
  private readonly environment: string;

  constructor(
    serviceName = "willpower-store-web",
    environment = process.env.NODE_ENV || "development"
  ) {
    this.serviceName = serviceName;
    this.environment = environment;
  }

  /**
   * Get current trace context for correlation
   */
  private getTraceContext() {
    if (typeof window === "undefined") return {};

    const activeSpan = trace.getActiveSpan();
    if (activeSpan) {
      const spanContext = activeSpan.spanContext();
      return {
        traceId: spanContext.traceId,
        spanId: spanContext.spanId,
      };
    }
    return {};
  }

  /**
   * Create base log entry with common fields
   */
  private createBaseEntry(level: LogLevel, message: string): BaseLogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      message,
      service: this.serviceName,
      environment: this.environment,
      ...this.getTraceContext(),
    };
  }

  /**
   * Check if we should log (avoid logging in production client-side unless explicitly enabled)
   */
  private shouldLog(level: LogLevel): boolean {
    if (typeof window === "undefined") return true; // Always log on server-side

    // In production, only log errors and warnings unless explicitly enabled
    if (
      this.environment === "production" &&
      process.env.NEXT_PUBLIC_ENABLE_CLIENT_LOGGING !== "true"
    ) {
      return level === LogLevel.ERROR || level === LogLevel.WARN;
    }

    return true;
  }

  /**
   * Log a general message
   */
  log(level: LogLevel, message: string, metadata?: Record<string, unknown>) {
    if (!this.shouldLog(level)) return;

    // Use different output format based on environment
    if (process.env.NODE_ENV === "development") {
      this.logDevelopment(level, message, metadata);
    } else {
      const entry = {
        ...this.createBaseEntry(level, message),
        ...metadata,
      };

      // Use console.log for Vercel Runtime Logs compatibility
      console.log(JSON.stringify(entry));
    }
  }

  /**
   * Development-friendly logging format for browser
   */
  private logDevelopment(
    level: LogLevel,
    message: string,
    metadata?: Record<string, unknown>
  ) {
    const timestamp = new Date().toLocaleTimeString();
    const levelColor = this.getLevelColor(level);

    // Format the main log line with browser-friendly styling
    const logArgs = [
      `%c[${level.toUpperCase()}]%c ${timestamp} ${message}`,
      `color: ${levelColor}; font-weight: bold;`,
      "color: inherit;",
    ];

    // Add metadata if present (stringified for console formatting)
    if (metadata && Object.keys(metadata).length > 0) {
      const filtered = this.filterSensitiveData(metadata);
      logArgs.push("\n", JSON.stringify(filtered, null, 2));
    }

    // Use appropriate console method
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(...logArgs);
        break;
      case LogLevel.INFO:
        console.info(...logArgs);
        break;
      case LogLevel.WARN:
        console.warn(...logArgs);
        break;
      case LogLevel.ERROR:
        console.error(...logArgs);
        break;
      default:
        console.log(...logArgs);
        break;
    }
  }

  /**
   * Get CSS colors for log levels in browser
   */
  private getLevelColor(level: LogLevel): string {
    switch (level) {
      case LogLevel.DEBUG:
        return "#00bcd4"; // Cyan
      case LogLevel.INFO:
        return "#4caf50"; // Green
      case LogLevel.WARN:
        return "#ff9800"; // Orange
      case LogLevel.ERROR:
        return "#f44336"; // Red
      default:
        return "#000000"; // Black
    }
  }

  /**
   * Filter out sensitive data from logs (client-side)
   */
  private filterSensitiveData(
    data: Record<string, unknown>
  ): Record<string, unknown> {
    const sensitiveKeys = [
      "password",
      "token",
      "secret",
      "key",
      "authorization",
    ];
    const filtered: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(data)) {
      if (
        sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))
      ) {
        filtered[key] = "[REDACTED]";
      } else if (typeof value === "object" && value !== null) {
        filtered[key] = this.filterSensitiveData(
          value as Record<string, unknown>
        );
      } else {
        filtered[key] = value;
      }
    }

    return filtered;
  }

  /**
   * Log info message
   */
  info(message: string, metadata?: Record<string, unknown>) {
    this.log(LogLevel.INFO, message, metadata);
  }

  /**
   * Log warning message
   */
  warn(message: string, metadata?: Record<string, unknown>) {
    this.log(LogLevel.WARN, message, metadata);
  }

  /**
   * Log error message
   */
  error(message: string, error?: Error, metadata?: Record<string, unknown>) {
    const errorMetadata = error
      ? {
          error: {
            name: error.name,
            message: error.message,
            stack: error.stack,
          },
        }
      : {};

    this.log(LogLevel.ERROR, message, {
      ...errorMetadata,
      ...metadata,
    });
  }

  /**
   * Log debug message (only in development)
   */
  debug(message: string, metadata?: Record<string, unknown>) {
    if (
      this.environment === "development" ||
      process.env.NEXT_PUBLIC_DEBUG === "true"
    ) {
      this.log(LogLevel.DEBUG, message, metadata);
    }
  }

  /**
   * Log API call from client
   */
  logAPICall(entry: APILogEntry["api"]) {
    const apiEntry: APILogEntry = {
      ...this.createBaseEntry(
        entry.status >= 400 ? LogLevel.ERROR : LogLevel.INFO,
        `API ${entry.method} ${entry.endpoint}`
      ),
      api: entry,
    };

    console.log(JSON.stringify(apiEntry));
  }

  /**
   * Log user interaction
   */
  logUserInteraction(entry: UserInteractionLogEntry["interaction"]) {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const interactionEntry: UserInteractionLogEntry = {
      ...this.createBaseEntry(
        LogLevel.INFO,
        `User ${entry.type}: ${entry.element || entry.page}`
      ),
      interaction: entry,
    };

    console.log(JSON.stringify(interactionEntry));
  }

  /**
   * Create a timed operation wrapper with automatic logging (client-side)
   */
  async withTiming<T>(
    operation: string,
    fn: () => Promise<T>,
    metadata?: Record<string, unknown>
  ): Promise<T> {
    const startTime = Date.now();

    // Only create spans if OpenTelemetry is available
    if (typeof window !== "undefined" && trace.getTracer) {
      const tracer = trace.getTracer("willpower-store-web");

      return tracer.startActiveSpan(
        operation,
        { kind: SpanKind.CLIENT },
        async (span) => {
          try {
            const result = await fn();
            const duration = Date.now() - startTime;

            span.setStatus({ code: SpanStatusCode.OK });
            span.setAttributes({
              "operation.name": operation,
              "operation.duration": duration,
              ...metadata,
            });

            this.info(`Operation completed: ${operation}`, {
              operation,
              duration,
              ...metadata,
            });

            return result;
          } catch (error) {
            const duration = Date.now() - startTime;

            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: error instanceof Error ? error.message : "Unknown error",
            });

            span.setAttributes({
              "operation.name": operation,
              "operation.duration": duration,
              "operation.error": true,
              ...metadata,
            });

            this.error(
              `Operation failed: ${operation}`,
              error instanceof Error ? error : new Error(String(error)),
              {
                operation,
                duration,
                ...metadata,
              }
            );

            throw error;
          } finally {
            span.end();
          }
        }
      );
    }
    // Fallback without OpenTelemetry
    try {
      const result = await fn();
      const duration = Date.now() - startTime;

      this.info(`Operation completed: ${operation}`, {
        operation,
        duration,
        ...metadata,
      });

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;

      this.error(
        `Operation failed: ${operation}`,
        error instanceof Error ? error : new Error(String(error)),
        {
          operation,
          duration,
          ...metadata,
        }
      );

      throw error;
    }
  }
}

// Export singleton logger instance
export const logger = new ClientLogger();

// Export types for external use
export type { APILogEntry, UserInteractionLogEntry };
